import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import PendingActionsIcon from '@mui/icons-material/PendingActionsOutlined';
import DraftsIcon from '@mui/icons-material/DraftsOutlined';
import PaymentsIcon from '@mui/icons-material/PaymentsOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLongOutlined';
import { apiClient } from '@/api/axiosClient';
import StatCard from '@/components/common/StatCard';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import ChartCard from '@/components/dashboard/ChartCard';
import DashboardBarChart from '@/components/dashboard/DashboardBarChart';
import DashboardLineChart from '@/components/dashboard/DashboardLineChart';
import DashboardPieChart from '@/components/dashboard/DashboardPieChart';
import { filterLastNDays, groupCountBy, groupSumByDay, topN } from '@/lib/dashboardAggregations';
import { useCategoryColors } from '@/pages/dashboard/dashboardColors';
import { withId } from '@/pages/reports/reportHelpers';
import type { InvoiceDto, VasChargeDto } from '@/types/billing';

const TREND_DAYS = 15;
const RECENT_DAYS = 30;
const CATEGORY_SLICE_COLORS = ['#0566d9', '#ffb951', '#4edea3'];
const STATUS_SLICE_COLORS = ['#bec6e0', '#0566d9', '#ffb951', '#4edea3', '#ba1a1a'];
const formatCurrency = (v: number) => `₹${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

interface ClientBillingRow {
  clientName: string;
  invoicedLast30Days: number;
  unpaidInvoiceCount: number;
  unbilledVasCount: number;
}

export default function BillingPanel() {
  const { t } = useTranslation('dashboard');
  const theme = useTheme();
  const colors = useCategoryColors();

  const { data: vasCharges = [] } = useQuery({
    queryKey: ['vas-charges'],
    queryFn: async () => (await apiClient.get<VasChargeDto[]>('/billing/vas-charges', { params: { status: 'Unbilled' } })).data,
  });
  const { data: invoices = [], isLoading: loadingInvoices } = useQuery({
    queryKey: ['invoices'],
    queryFn: async () => (await apiClient.get<InvoiceDto[]>('/billing/invoices')).data,
  });

  const draftInvoices = invoices.filter((i) => i.status === 'Draft');
  const now = new Date();
  const thisMonthBilled = invoices
    .filter((i) => i.status !== 'Draft' && new Date(i.invoiceDate).getMonth() === now.getMonth() && new Date(i.invoiceDate).getFullYear() === now.getFullYear())
    .reduce((sum, i) => sum + i.totalAmount, 0);
  // No DueDate field exists on Invoice (see Domain/Billing/Invoice.cs) — "overdue" isn't
  // computable, so this tracks unpaid (Finalized/Sent, not yet Paid/Cancelled) instead, the same
  // metric ClientPortalDashboardSummary already surfaces on the Client's own side.
  const unpaidInvoices = invoices.filter((i) => i.status === 'Finalized' || i.status === 'Sent');

  const cards = [
    { label: t('billingPanel.unbilledVas'), value: vasCharges.length, icon: <PendingActionsIcon />, accentColor: '#ffb951' },
    { label: t('billingPanel.draftInvoices'), value: draftInvoices.length, icon: <DraftsIcon />, accentColor: '#bec6e0' },
    {
      label: t('billingPanel.thisMonthBilled'),
      value: formatCurrency(thisMonthBilled),
      icon: <PaymentsIcon />,
      accentColor: '#4edea3',
    },
    { label: t('billingPanel.unpaidInvoices'), value: unpaidInvoices.length, icon: <ReceiptLongIcon />, accentColor: '#ffb4ab' },
  ];

  // 15-day daily trend — billed amount (non-Draft invoices) + VAS charges raised, day by day,
  // zero-filled so it reads as a continuous timeline instead of skipping quiet days.
  const dailyTrend = useMemo(
    () =>
      groupSumByDay(
        invoices.filter((i) => i.status !== 'Draft'),
        (i) => i.invoiceDate,
        [{ key: 'billed', value: (i) => i.totalAmount }],
        TREND_DAYS,
      ),
    [invoices],
  );
  const dailyVasTrend = useMemo(
    () => groupSumByDay(vasCharges, (v) => v.chargeDate, [{ key: 'vas', value: (v) => v.amount }], TREND_DAYS),
    [vasCharges],
  );
  const hasTrendData = dailyTrend.some((d) => d.values.billed > 0) || dailyVasTrend.some((d) => d.values.vas > 0);

  const invoiceStatusDistribution = useMemo(() => groupCountBy(invoices, (i) => i.status), [invoices]);

  // Charge-category split (Storage/Handling/VAS) across every line of every non-Draft invoice —
  // the one place this breakdown is visible at a glance instead of buried inside each invoice.
  const categoryBreakdown = useMemo(() => {
    const totals = new Map<string, number>();
    for (const inv of invoices) {
      if (inv.status === 'Draft') continue;
      for (const line of inv.lines) {
        totals.set(line.chargeCategory, (totals.get(line.chargeCategory) ?? 0) + line.amount);
      }
    }
    return Array.from(totals.entries()).map(([key, total]) => ({ key, total }));
  }, [invoices]);

  const topClients = useMemo(() => {
    const totals = new Map<string, number>();
    for (const inv of invoices) {
      if (inv.status === 'Draft') continue;
      totals.set(inv.clientName, (totals.get(inv.clientName) ?? 0) + inv.totalAmount);
    }
    return topN(Array.from(totals.entries()).map(([clientName, total]) => ({ clientName, total })), (r) => r.total, 8);
  }, [invoices]);

  // Per-client "last 30 days" scorecard — invoiced amount raised in the window, plus this client's
  // current outstanding position (unpaid invoices, unbilled VAS) regardless of when those were
  // raised, since "what do I still owe/need to bill" doesn't reset every 30 days.
  const clientRows = useMemo<ClientBillingRow[]>(() => {
    const recentInvoices = filterLastNDays(invoices, (i) => i.invoiceDate, RECENT_DAYS).filter((i) => i.status !== 'Draft');
    const byClient = new Map<string, ClientBillingRow>();
    const get = (name: string) => byClient.get(name) ?? { clientName: name, invoicedLast30Days: 0, unpaidInvoiceCount: 0, unbilledVasCount: 0 };

    for (const inv of recentInvoices) {
      const entry = get(inv.clientName);
      entry.invoicedLast30Days += inv.totalAmount;
      byClient.set(inv.clientName, entry);
    }
    for (const inv of unpaidInvoices) {
      const entry = get(inv.clientName);
      entry.unpaidInvoiceCount += 1;
      byClient.set(inv.clientName, entry);
    }
    for (const v of vasCharges) {
      const entry = get(v.clientName);
      entry.unbilledVasCount += 1;
      byClient.set(v.clientName, entry);
    }

    return Array.from(byClient.values()).sort((a, b) => b.invoicedLast30Days - a.invoicedLast30Days);
  }, [invoices, unpaidInvoices, vasCharges]);

  const clientColumns: DataTableColumn<ClientBillingRow>[] = [
    { field: 'clientName', headerName: t('billingPanel.table.client') },
    { field: 'invoicedLast30Days', headerName: t('billingPanel.table.invoicedLast30Days'), align: 'right', render: (r) => formatCurrency(r.invoicedLast30Days) },
    { field: 'unpaidInvoiceCount', headerName: t('billingPanel.table.unpaidInvoiceCount'), align: 'right' },
    { field: 'unbilledVasCount', headerName: t('billingPanel.table.unbilledVasCount'), align: 'right' },
  ];

  return (
    <Stack spacing={3}>
      <GridContainer spacing={2} columns={4}>
        {cards.map((card) => (
          <GridItem xs={2} md={1} key={card.label}>
            <StatCard {...card} />
          </GridItem>
        ))}
      </GridContainer>

      <GridContainer columns={12} spacing={2}>
        <GridItem xs={12} md={7}>
          <ChartCard title={t('billingPanel.trendTitle')} subtitle={t('billingPanel.trendSubtitle')} loading={loadingInvoices} empty={!loadingInvoices && !hasTrendData}>
            <DashboardLineChart
              categories={dailyTrend.map((d) => d.day)}
              series={[
                { label: t('billingPanel.billedSeries'), data: dailyTrend.map((d) => d.values.billed), color: colors.billing, area: true },
                { label: t('billingPanel.vasSeries'), data: dailyVasTrend.map((d) => d.values.vas), color: theme.palette.info.main },
              ]}
            />
          </ChartCard>
        </GridItem>
        <GridItem xs={12} md={5}>
          <ChartCard title={t('billingPanel.statusTitle')} loading={loadingInvoices} empty={!loadingInvoices && invoiceStatusDistribution.length === 0}>
            <DashboardPieChart data={invoiceStatusDistribution.map((s, i) => ({ label: s.key, value: s.count, color: STATUS_SLICE_COLORS[i % STATUS_SLICE_COLORS.length] }))} />
          </ChartCard>
        </GridItem>
      </GridContainer>

      <GridContainer columns={12} spacing={2}>
        <GridItem xs={12} md={5}>
          <ChartCard title={t('billingPanel.categoryTitle')} loading={loadingInvoices} empty={!loadingInvoices && categoryBreakdown.length === 0}>
            <DashboardPieChart data={categoryBreakdown.map((c, i) => ({ label: c.key, value: c.total, color: CATEGORY_SLICE_COLORS[i % CATEGORY_SLICE_COLORS.length] }))} />
          </ChartCard>
        </GridItem>
        <GridItem xs={12} md={7}>
          <ChartCard title={t('billingPanel.topClientsTitle')} loading={loadingInvoices} empty={!loadingInvoices && topClients.length === 0}>
            <DashboardBarChart
              layout="horizontal"
              categories={topClients.map((c) => c.clientName)}
              series={[{ label: t('billingPanel.table.invoicedLast30Days'), data: topClients.map((c) => c.total), color: colors.billing }]}
            />
          </ChartCard>
        </GridItem>
      </GridContainer>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
          {t('billingPanel.clientTableTitle')}
        </Typography>
        <DataTable
          columns={clientColumns}
          rows={withId(clientRows, (r) => r.clientName)}
          loading={loadingInvoices}
          searchable
          exportFileName="client-billing-summary"
        />
      </Paper>
    </Stack>
  );
}
